import type { FC, ReactNode } from "react";
import { Autocomplete, Dialog, ListBox } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  SearchModalCenteredState,
  type SearchModalEmptyState,
} from "./searchModalCenteredState.js";
import { SearchModalHeader } from "./searchModalHeader.js";
import { SearchModalListBoxSection } from "./searchModalListBoxSection.js";
import { SearchModalLoadingState } from "./searchModalLoadingState.js";
import {
  SearchModalRow,
  type SearchModalRowDensity,
} from "./searchModalRow.js";

export interface SearchModalAction {
  id: string;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  onSelect: () => void;
}

export interface SearchModalResult {
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  trailing?: ReactNode;
  icon?: ReactNode;
  density?: SearchModalRowDensity;
  onSelect?: () => void;
}

export interface SearchModalSectionData {
  id: string;
  title?: string;
  results: readonly SearchModalResult[];
  /**
   * The rows this section is going to show, for the skeleton to stand in for
   * while they load. Declared alongside the results rather than beside them, so
   * a placeholder can't promise a row shape the section never renders.
   */
  placeholder?: SearchModalSectionPlaceholder;
}

export interface SearchModalSectionPlaceholder {
  rowCount?: number;
  hasBadge?: boolean;
  hasTrailing?: boolean;
}

export interface SearchModalProps {
  label?: string;
  query: string;
  placeholder: string;
  maxLength?: number;
  sections: readonly SearchModalSectionData[];
  recentSearches?: readonly SearchModalAction[];
  allResultsAction?: SearchModalAction;
  isLoading?: boolean;
  /**
   * Shown when a query returns nothing. Omit when the modal always offers a
   * next step (e.g. the all-results action), which the design prefers over a
   * dead-end message.
   */
  noResultsState?: SearchModalEmptyState;
  className?: string;
  autoFocus?: boolean;
  showCloseButton?: boolean;
  onQueryChange: (query: string) => void;
  onClose?: () => void;
  onSelectResult?: (result: SearchModalResult) => void;
}

/**
 * The command-k search surface. An `Autocomplete` wires the search field to a
 * single `ListBox` holding every actionable row (the all-results action,
 * recent searches, and result sections), so Arrow Up/Down virtually focus rows
 * from the input and Enter selects the focused row. Searching a query focuses
 * the all-results action, making it the default Enter target.
 */
export const SearchModal: FC<SearchModalProps> = (props) => {
  const {
    label = "Search",
    query,
    placeholder,
    maxLength,
    sections,
    recentSearches = [],
    allResultsAction,
    isLoading = false,
    noResultsState,
    className,
    autoFocus,
    showCloseButton,
    onQueryChange,
    onClose,
    onSelectResult,
  } = props;

  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length > 0;
  const visibleSections = sections.filter((section) => section.results.length);
  const hasResults = visibleSections.length > 0;

  // The all-results action and the result sections alike yield to the loading
  // skeleton: the action is only worth choosing once the user has seen what the
  // faster search already found.
  const showAllResultsAction =
    hasQuery && !isLoading && allResultsAction !== undefined;
  const showRecentSearches = !hasQuery && recentSearches.length > 0;
  const showResultSections = !isLoading && hasResults;
  const hasListBoxRows =
    showAllResultsAction || showRecentSearches || showResultSections;
  const visibleNoResultsState =
    !isLoading && hasQuery && !hasResults ? noResultsState : undefined;
  // Nothing to show below the field — the modal collapses to just the bar.
  const hasBody =
    hasListBoxRows || isLoading || visibleNoResultsState !== undefined;

  return (
    <Dialog
      aria-label={label}
      className={twMerge(
        "flex w-[600px] flex-col overflow-hidden bg-popover",
        // Fixed while there is a body, so rows arriving and leaving don't
        // resize the modal under the pointer. Tall enough for a full history —
        // three recent searches and three recently viewed results — which would
        // otherwise scroll by a couple of pixels; a query's results are meant to
        // scroll, and do.
        hasBody && "h-[440px]",
        className,
      )}
    >
      <Autocomplete
        inputValue={query}
        onInputChange={onQueryChange}
        // Off so the default row is the listbox's autofocus below and nothing
        // else: react-aria's own "focus the first item" only fires for typing
        // forward, into a listbox that stayed mounted, which made the default
        // depend on how the query got there.
        disableAutoFocusFirst
      >
        <SearchModalHeader
          placeholder={placeholder}
          maxLength={maxLength}
          autoFocus={autoFocus}
          showCloseButton={showCloseButton}
          showDivider={hasBody}
          onClose={onClose}
        />

        {hasBody ? (
          <div
            // Rows sit flush across the listbox and the skeleton alike, so the
            // placeholder groups land where the loaded groups will.
            className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto px-5 pt-3 pb-4"
          >
            {hasListBoxRows ? (
              <ListBox
                // The all-results action is the one row ever highlighted on the
                // user's behalf, and it leads the list, so "focus the first
                // item" makes it the default Enter target. Gated on the row
                // being on screen, since "first" would otherwise resolve to a
                // result. Autofocus applies once per listbox, so every input
                // value gets its own: editing the query defaults back to the
                // action even when rows stay on screen throughout (and the user
                // had arrowed away from it).
                key={showAllResultsAction ? query : "rows"}
                autoFocus={showAllResultsAction ? "first" : undefined}
                aria-label="Search results"
                // Rows sit flush; titled sections bring their own separation.
                className="flex shrink-0 flex-col gap-px outline-none"
              >
                {showAllResultsAction ? (
                  <SearchModalRow
                    id={allResultsAction.id}
                    density={allResultsAction.subtitle ? "regular" : "compact"}
                    icon={allResultsAction.icon}
                    title={allResultsAction.title}
                    subtitle={allResultsAction.subtitle}
                    onAction={allResultsAction.onSelect}
                  />
                ) : null}
                {showRecentSearches ? (
                  <SearchModalListBoxSection
                    id="recent-searches"
                    title="Recent searches"
                  >
                    {recentSearches.map((search) => (
                      <SearchModalRow
                        key={search.id}
                        id={search.id}
                        density="compact"
                        icon={search.icon}
                        title={search.title}
                        onAction={search.onSelect}
                      />
                    ))}
                  </SearchModalListBoxSection>
                ) : null}
                {showResultSections
                  ? visibleSections.map((section) => (
                      <SearchModalListBoxSection
                        key={section.id}
                        id={section.id}
                        title={section.title}
                      >
                        {section.results.map((result) => (
                          <SearchModalRow
                            key={result.id}
                            id={result.id}
                            title={result.title}
                            subtitle={result.subtitle}
                            badge={result.badge}
                            trailing={result.trailing}
                            icon={result.icon}
                            density={result.density}
                            onAction={() => {
                              result.onSelect?.();
                              onSelectResult?.(result);
                            }}
                          />
                        ))}
                      </SearchModalListBoxSection>
                    ))
                  : null}
              </ListBox>
            ) : null}
            {isLoading ? (
              // Every declared section gets a placeholder, not just the ones
              // with results: which of them the query will fill is exactly what
              // isn't known yet.
              <SearchModalLoadingState
                sections={sections}
                hasAllResultsAction={hasQuery && allResultsAction !== undefined}
              />
            ) : null}
            {/*
              A query with nothing to show only gets a message when the caller
              supplied one; otherwise the all-results action above is the next
              step and a "no results" line would just be noise.
            */}
            {visibleNoResultsState ? (
              <SearchModalCenteredState state={visibleNoResultsState} />
            ) : null}
          </div>
        ) : null}
      </Autocomplete>
    </Dialog>
  );
};
